/**
 * AppError - Wrapper class for platform-wide error handling
 * Extends Error with structured context for logging, serialization, and multi-tenant isolation
 */

import { ErrorResponse, ErrorDocument, ErrorCategory, ErrorSeverity } from './errors';
import { ErrorIdGenerator } from './ErrorIdGenerator';

/**
 * AppError: In-flight error representation during Lambda/API execution
 * Provides methods to serialize to ErrorResponse, CloudWatch JSON, and DynamoDB documents
 */
export class AppError extends Error {
  readonly errorId: string;
  readonly statusCode: number;
  readonly userMessage: string;
  readonly technicalMessage: string;
  readonly category: ErrorCategory;
  readonly severity: ErrorSeverity;
  readonly retryable: boolean;
  readonly context: Record<string, unknown>;
  readonly timestamp: string;

  constructor(options: {
    statusCode: number;
    userMessage: string;
    technicalMessage: string;
    category: ErrorCategory;
    severity?: ErrorSeverity;
    retryable?: boolean;
    context?: Record<string, unknown>;
    cause?: Error;
  }) {
    super(options.technicalMessage);

    // Maintain proper Error prototype chain for instanceof checks
    Object.setPrototypeOf(this, AppError.prototype);

    this.name = this.constructor.name;
    this.statusCode = options.statusCode;
    this.userMessage = options.userMessage;
    this.technicalMessage = options.technicalMessage;
    this.category = options.category;
    this.severity =
      options.severity ||
      AppError.getDefaultSeverityForCategory(options.category);
    this.retryable = options.retryable ?? false;
    this.context = options.context || {};
    this.timestamp = new Date().toISOString();

    // Generate deterministic ErrorId based on error type, message, and context
    this.errorId = ErrorIdGenerator.generate({
      errorType: this.constructor.name,
      message: options.technicalMessage,
      context: options.context || {},
    });

    // Preserve cause error if provided
    if (options.cause && typeof options.cause === 'object') {
      Object.assign(this, { cause: options.cause });
    }

    // Capture stack trace
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor);
    }
  }

  /**
   * Convert to ErrorResponse for API clients
   */
  toErrorResponse(): ErrorResponse {
    return {
      statusCode: this.statusCode,
      errorId: this.errorId,
      userMessage: this.userMessage,
      timestamp: this.timestamp,
      retryable: this.retryable,
      category: this.category,
      severity: this.severity,
      context: this.context,
    };
  }

  /**
   * Convert to DynamoDB ErrorDocument for storage
   * Call from Lambda handler with request context
   */
  toDynamoDBDocument(options: {
    ownerId: string;
    lambdaName: string;
    requestId: string;
    xrayTraceId: string;
    requestPath: string;
    requestMethod: string;
    requestBody?: Record<string, unknown>;
    sourceIp: string;
    sessionId?: string;
  }): ErrorDocument {
    return {
      ownerId: options.ownerId,
      timestamp: this.timestamp,
      errorId: this.errorId,
      category: this.category,
      severity: this.severity,
      errorType: this.constructor.name,
      userMessage: this.userMessage,
      technicalMessage: this.technicalMessage,
      stack: this.stack,
      context: this.context,
      requestPath: options.requestPath,
      requestMethod: options.requestMethod,
      requestBody: options.requestBody,
      responseStatus: this.statusCode,
      retryable: this.retryable,
      lambdaName: options.lambdaName,
      requestId: options.requestId,
      xrayTraceId: options.xrayTraceId,
      sourceIp: options.sourceIp,
      sessionId: options.sessionId,
    };
  }

  /**
   * Convert to CloudWatch log entry (single-line JSON)
   */
  toCloudWatchLog(options: {
    ownerId: string;
    lambdaName: string;
    requestId: string;
    xrayTraceId: string;
  }): string {
    const logEntry = {
      errorId: this.errorId,
      ownerId: options.ownerId,
      timestamp: this.timestamp,
      severity: this.severity,
      category: this.category,
      message: this.technicalMessage,
      errorType: this.constructor.name,
      statusCode: this.statusCode,
      lambda: options.lambdaName,
      requestId: options.requestId,
      xrayTraceId: options.xrayTraceId,
      context: this.context,
      stack: this.stack?.split('\n').slice(0, 5).join(' | '),
    };

    return JSON.stringify(logEntry);
  }

  /**
   * Get default severity level for a category
   */
  private static getDefaultSeverityForCategory(
    category: ErrorCategory,
  ): ErrorSeverity {
    switch (category) {
      case 'VALIDATION':
        return 'WARN';
      case 'AUTH':
        return 'WARN';
      case 'USER':
        return 'INFO';
      case 'EXTERNAL':
        return 'WARN';
      case 'FINANCIAL':
        return 'ERROR';
      case 'SYSTEM':
      default:
        return 'ERROR';
    }
  }
}

/**
 * Common error factory functions for standard scenarios
 */
export class AppErrorFactory {
  /**
   * Validation error (400)
   */
  static validation(
    userMessage: string,
    technicalMessage?: string,
    context?: Record<string, unknown>,
  ): AppError {
    return new AppError({
      statusCode: 400,
      userMessage,
      technicalMessage: technicalMessage || userMessage,
      category: 'VALIDATION',
      severity: 'WARN',
      retryable: false,
      context,
    });
  }

  /**
   * Authentication error (401)
   */
  static unauthorized(
    userMessage: string = 'Your session expired. Please log in again.',
    context?: Record<string, unknown>,
  ): AppError {
    return new AppError({
      statusCode: 401,
      userMessage,
      technicalMessage: userMessage,
      category: 'AUTH',
      severity: 'WARN',
      retryable: false,
      context,
    });
  }

  /**
   * Authorization error (403)
   */
  static forbidden(
    userMessage: string = 'You do not have permission to access this resource.',
    technicalMessage?: string,
    context?: Record<string, unknown>,
  ): AppError {
    return new AppError({
      statusCode: 403,
      userMessage,
      technicalMessage: technicalMessage || userMessage,
      category: 'AUTH',
      severity: 'WARN',
      retryable: false,
      context,
    });
  }

  /**
   * Not found error (404)
   */
  static notFound(
    userMessage: string = 'Resource not found.',
    context?: Record<string, unknown>,
  ): AppError {
    return new AppError({
      statusCode: 404,
      userMessage,
      technicalMessage: userMessage,
      category: 'VALIDATION',
      severity: 'WARN',
      retryable: false,
      context,
    });
  }

  /**
   * External service error (503) - retryable
   */
  static externalError(
    userMessage: string = 'Service is temporarily busy. Please try again.',
    technicalMessage?: string,
    context?: Record<string, unknown>,
  ): AppError {
    return new AppError({
      statusCode: 503,
      userMessage,
      technicalMessage: technicalMessage || userMessage,
      category: 'EXTERNAL',
      severity: 'WARN',
      retryable: true,
      context,
    });
  }

  /**
   * DynamoDB throttle error (503) - retryable
   */
  static throttled(
    technicalMessage: string,
    context?: Record<string, unknown>,
  ): AppError {
    return new AppError({
      statusCode: 503,
      userMessage: 'Service is temporarily busy. Please try again.',
      technicalMessage,
      category: 'EXTERNAL',
      severity: 'WARN',
      retryable: true,
      context,
    });
  }

  /**
   * Unhandled system error (500)
   */
  static internal(
    userMessage: string = 'Something went wrong. Please try again.',
    technicalMessage?: string,
    cause?: Error,
    context?: Record<string, unknown>,
  ): AppError {
    return new AppError({
      statusCode: 500,
      userMessage,
      technicalMessage: technicalMessage || userMessage,
      category: 'SYSTEM',
      severity: 'ERROR',
      retryable: false,
      context,
      cause,
    });
  }

  /**
   * Financial operation error
   */
  static financial(
    userMessage: string,
    technicalMessage: string,
    statusCode: number = 400,
    context?: Record<string, unknown>,
  ): AppError {
    return new AppError({
      statusCode,
      userMessage,
      technicalMessage,
      category: 'FINANCIAL',
      severity: 'ERROR',
      retryable: false,
      context,
    });
  }

  /**
   * Critical financial error
   */
  static financialCritical(
    userMessage: string,
    technicalMessage: string,
    context?: Record<string, unknown>,
  ): AppError {
    return new AppError({
      statusCode: 500,
      userMessage,
      technicalMessage,
      category: 'FINANCIAL',
      severity: 'CRITICAL',
      retryable: false,
      context,
    });
  }
}
